import React, { useEffect, useState } from "react";
import { Mic, Loader2, Sparkles, AudioLines } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";

export const LangAnchorsTab = ({ stationId }) => {
  const [anchors, setAnchors] = useState([]);
  const [anchorId, setAnchorId] = useState("");
  const [script, setScript] = useState("");
  const [topic, setTopic] = useState("Top-of-hour news bulletin");
  const [busyScript, setBusyScript] = useState(false);
  const [busyRender, setBusyRender] = useState(false);
  const [bulletins, setBulletins] = useState([]);

  useEffect(() => {
    api.get("/language-center/anchors").then((r) => {
      setAnchors(r.data || []);
      if (r.data?.length) setAnchorId(r.data[0].id);
    }).catch(() => {});
    api.get("/language-center/anchors/bulletins").then((r) => setBulletins(r.data || [])).catch(() => {});
  }, []);

  const anchor = anchors.find((a) => a.id === anchorId);

  const autoScript = async () => {
    if (!anchor) return;
    setBusyScript(true);
    try {
      const r = await api.post("/language-center/generate", {
        station_id: stationId, content_type: "radio_script", topic, languages: [anchor.language] });
      const item = (r.data.items || [])[0];
      if (item?.output) { setScript(item.output); toast.success(`Script written in ${anchor.language_name}`); }
      else toast.error(item?.error || "Script generation failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Script generation failed");
    } finally { setBusyScript(false); }
  };

  const render = async () => {
    if (!anchor) return;
    if (!script.trim()) return toast.error("Write or auto-generate a script first");
    setBusyRender(true);
    try {
      const r = await api.post("/language-center/anchors/render", {
        station_id: stationId, anchor_id: anchorId, script });
      setBulletins((p) => [r.data, ...p]);
      toast.success("Bulletin rendered");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Render failed");
    } finally { setBusyRender(false); }
  };

  return (
    <div data-testid="lc-anchors-tab">
      <h2 className="font-display text-xl font-bold text-ink-primary">AI Voice Anchor Studio</h2>
      <p className="text-sm text-ink-secondary mb-6">Language-specific AI news anchors for every market</p>
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">Select News Anchor</div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {anchors.map((a) => (
              <button key={a.id} onClick={() => setAnchorId(a.id)} data-testid={`lc-anchor-${a.id}`}
                className={`w-full flex items-center gap-3 rounded-xl border p-3.5 text-left transition ${
                  anchorId === a.id ? "border-brand-amber/50 bg-brand-amber/5" : "border-line bg-bg-surface hover:border-ink-muted/40"}`}>
                <span className="font-display text-sm font-bold text-ink-primary uppercase w-10 shrink-0">{a.language}</span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-ink-primary">{a.label}</span>
                  <span className="block text-xs text-ink-muted">{a.desc}</span>
                </span>
                {a.caribbean && (
                  <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[8px] font-mono uppercase tracking-wider text-emerald-300">Caribbean</span>
                )}
              </button>
            ))}
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">News Script</div>
              <button onClick={autoScript} disabled={busyScript || !anchor} data-testid="lc-anchor-autoscript-btn"
                className="inline-flex items-center gap-1 rounded-md border border-line px-2.5 py-1 text-[11px] text-ink-secondary hover:text-ink-primary transition disabled:opacity-50">
                {busyScript ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                Auto-generate in {anchor?.language_name || ""}
              </button>
            </div>
            <input
              value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="lc-anchor-topic"
              placeholder="Script topic (used by auto-generate)"
              className="w-full rounded-lg border border-line bg-bg-surface px-3 py-2 text-xs text-ink-primary placeholder:text-ink-muted mb-2 focus:outline-none focus:border-brand-amber/50"
            />
            <textarea
              value={script} onChange={(e) => setScript(e.target.value)} rows={6} data-testid="lc-anchor-script"
              placeholder="Paste or auto-generate the bulletin script the anchor should read…"
              className="w-full rounded-xl border border-line bg-bg-surface p-3 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/50"
            />
            <button onClick={render} disabled={busyRender} data-testid="lc-anchor-render-btn"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-3 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-60">
              {busyRender ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
              {busyRender ? "Rendering audio…" : "Render Audio Bulletin"}
            </button>
          </div>
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">Generated Audio Bulletins</div>
          {bulletins.length === 0 ? (
            <div className="rounded-xl border border-dashed border-line p-12 text-center text-sm text-ink-muted">
              <AudioLines className="h-8 w-8 mx-auto mb-2 opacity-50" />
              Generated bulletins will appear here
            </div>
          ) : (
            <div className="space-y-3">
              {bulletins.map((b) => (
                <div key={b.asset_id} className="rounded-xl border border-line bg-bg-surface p-3.5" data-testid={`lc-bulletin-${b.asset_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-ink-primary flex-1 truncate">{b.label}</span>
                    {b.duration_seconds && <span className="font-mono text-[10px] text-ink-muted">{b.duration_seconds}s</span>}
                  </div>
                  <audio controls preload="none" className="w-full h-9" src={`${API_BASE}/imaging/assets/${b.asset_id}/audio`} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
